import { SignJWT, importPKCS8 } from 'jose';
import key from '../config/service-account.json';

const PROJECT_ID = key.project_id;
const BQ_ENDPOINT = `https://bigquery.googleapis.com/bigquery/v2/projects/${PROJECT_ID}/queries`;
const TOKEN_URI = 'https://oauth2.googleapis.com/token';
const SCOPE = 'https://www.googleapis.com/auth/bigquery';

let cachedToken: { token: string; exp: number } | null = null;
let cryptoKeyPromise: Promise<CryptoKey> | null = null;

async function getPrivateKey(): Promise<CryptoKey> {
  if (!cryptoKeyPromise) {
    cryptoKeyPromise = importPKCS8(key.private_key, 'RS256');
  }
  return cryptoKeyPromise;
}

async function getAccessToken(): Promise<string> {
  const now = Math.floor(Date.now() / 1000);
  if (cachedToken && cachedToken.exp - 60 > now) {
    return cachedToken.token;
  }

  const privateKey = await getPrivateKey();
  const assertion = await new SignJWT({ scope: SCOPE })
    .setProtectedHeader({ alg: 'RS256', typ: 'JWT' })
    .setIssuer(key.client_email)
    .setSubject(key.client_email)
    .setAudience(TOKEN_URI)
    .setIssuedAt(now)
    .setExpirationTime(now + 3600)
    .sign(privateKey);

  const res = await fetch(TOKEN_URI, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'urn:ietf:params:oauth:grant-type:jwt-bearer',
      assertion
    })
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Token fetch failed: ${res.status} ${text}`);
  }

  const json = (await res.json()) as { access_token: string; expires_in: number };
  cachedToken = { token: json.access_token, exp: now + (json.expires_in ?? 3600) };
  return cachedToken.token;
}

interface QueryOptions {
  query: string;
  params?: Record<string, unknown>;
}

export async function runQuery<T>(options: QueryOptions): Promise<T[]> {
  const { query, params } = options;
  const token = await getAccessToken();
  const res = await fetch(BQ_ENDPOINT, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      query,
      useLegacySql: false,
      parameterMode: 'NAMED',
      queryParameters: params
        ? Object.entries(params).map(([name, value]) => ({
            name,
            parameterType: { type: typeof value === 'number' ? 'FLOAT' : 'STRING' },
            parameterValue: { value }
          }))
        : []
    })
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`BQ query failed: ${res.status} ${text}`);
  }

  const json = (await res.json()) as {
    rows?: Array<{ f: Array<{ v: unknown }> }>;
    schema?: { fields: Array<{ name: string }> };
  };

  const fields = json.schema?.fields ?? [];
  const rows = json.rows ?? [];
  return rows.map((row) => {
    const obj: Record<string, unknown> = {};
    row.f.forEach((cell, idx) => {
      const field = fields[idx];
      if (field) obj[field.name] = cell.v;
    });
    return obj as T;
  });
}
